import { readonly, ref, toValue, type MaybeRefOrGetter, type Ref } from 'vue';
import { useApiModel } from '~/composables/useApiModel';
import type { Campaign, Candidate, Lobbyist, Party } from '~/types/Candidates';
import type { ApiError, ApiMedia } from '~/types/plugins/api';
import { mapSourceLink, type ApiSourceLike } from '~/utils/mapSourceLink';
import { normalizeExternalUrl } from '~/utils/normalizeExternalUrl';

interface ApiPartyCandidate {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  slug: string;
  position: 'lower' | 'upper';
  biography: string | null;
  is_incumbent: boolean;
  image: ApiMedia | null;
}

interface ApiPartyCampaign {
  id: number;
  title?: string;
  name?: string;
  slug: string;
  type: 'community' | 'party';
  description: string | null;
  // Present since VTW-461/VTW-496: whether this campaign is the party's own
  // direct link, or one it only reaches via one of its candidates.
  association_source?: 'direct' | 'inherited';
}

interface ApiPartyLobbyist {
  id: number;
  name: string;
  slug: string;
  industry: string | null;
  description?: string | null;
  website?: string | null;
  logo: ApiMedia | null;
}

interface ApiParty {
  id: number;
  name: string;
  abbreviation: string | null;
  slug: string;
  type: 'major' | 'minor';
  description: string | null;
  website: string | null;
  logo: ApiMedia | null;
  candidates?: ApiPartyCandidate[];
  campaigns?: ApiPartyCampaign[];
  lobbyists?: ApiPartyLobbyist[];
  sources?: ApiSourceLike[];
}

function mapParty(raw: ApiParty): Party {
  return {
    id: raw.slug,
    name: raw.name,
    abbreviation: raw.abbreviation ?? '',
    logo: raw.logo?.thumb_url ?? raw.logo?.url ?? null,
    classification: raw.type,
    description: raw.description ?? '',
    // `candidates_count` is always null on the show endpoint (only populated
    // via `include` on the list endpoint) — the nested `candidates` array is
    // normally present, but degrade to 0 rather than throwing if it's ever
    // omitted (e.g. relation not eager-loaded).
    candidatesCount: raw.candidates?.length ?? 0,
    campaignsCount: raw.campaigns?.length ?? 0,
    lobbyistsCount: raw.lobbyists?.length ?? 0,
    website: raw.website ?? '',
    sources: (raw.sources ?? []).map(mapSourceLink),
  };
}

function mapCandidate(
  raw: ApiPartyCandidate,
  partyName: string,
  partySlug: string,
): Candidate {
  const initials =
    `${raw.first_name.charAt(0)}${raw.last_name.charAt(0)}`.toUpperCase();

  return {
    id: raw.slug,
    name: raw.full_name,
    initials,
    image: raw.image?.thumb_url ?? raw.image?.url ?? null,
    // The nested candidate shape on the party-show endpoint has an empty
    // `party`/`region`/`electorate` array (unloaded) and no per-candidate
    // party name — this candidate belongs to the party being viewed, so its
    // name is used directly.
    party: partyName,
    partySlug,
    incumbent: raw.is_incumbent,
    chamber: raw.position,
    // Not available on this nested shape (`electorate: []` means unloaded).
    // Known gap — the party page's candidate cards don't render electorate.
    electorate: '',
    bio: raw.biography ?? '',
    // No website field on the Candidate resource at all.
    website: '',
    // Not available on this nested shape (`campaigns_count`/`lobbyists_count`
    // are always null here) — CandidateCard renders these counts, so every
    // candidate on the party page will show 0 for both. Accepted known
    // limitation for this slice.
    campaignsCount: 0,
    lobbyistsCount: 0,
    // Not available on this nested shape.
    sources: [],
  };
}

function mapPartyCampaign(raw: ApiPartyCampaign): Campaign {
  return {
    id: raw.slug,
    title: raw.title ?? raw.name ?? '',
    type: raw.type,
    description: raw.description ?? '',
    candidateIds: [],
    tags: [],
    // Not available on this nested shape.
    sources: [],
    // The Show Party response nests the party's own campaigns alongside ones
    // it only reaches via a candidate (VTW-461/VTW-496) — `type` can't tell
    // those apart, it classifies the campaign, not who leads it here.
    leadSource: raw.association_source === 'inherited' ? 'candidate' : 'party',
  };
}

// The API already orders party-led campaigns first (VTW-461), but the
// candidate profile's equivalent mapper doesn't rely on API order for this,
// so neither does this one — a partition rather than a sort, to keep each
// group's relative order intact.
function orderByLeadSource(campaigns: Campaign[]): Campaign[] {
  return [
    ...campaigns.filter((c) => c.leadSource !== 'candidate'),
    ...campaigns.filter((c) => c.leadSource === 'candidate'),
  ];
}

function mapPartyLobbyist(raw: ApiPartyLobbyist): Lobbyist {
  return {
    id: raw.slug,
    name: raw.name,
    industry: raw.industry ?? '',
    description: raw.description?.trim() || null,
    url: normalizeExternalUrl(raw.website),
    logo: raw.logo?.thumb_url ?? raw.logo?.url ?? null,
    candidateIds: [],
  };
}

export function useParty(id: MaybeRefOrGetter<string>) {
  const party = ref<Party | null>(null);
  const candidates = ref<Candidate[]>([]);
  const campaigns = ref<Campaign[]>([]);
  const lobbyists = ref<Lobbyist[]>([]);
  const pending = ref(true);
  const error = ref<ApiError | null>(null);

  const model = useApiModel<ApiParty>('parties', { autoLoad: false });

  (async () => {
    try {
      const raw = await model.find(toValue(id));
      if (raw) {
        const mappedParty = mapParty(raw);
        party.value = mappedParty;
        candidates.value = (raw.candidates ?? []).map((c) =>
          mapCandidate(c, mappedParty.name, mappedParty.id),
        );
        campaigns.value = orderByLeadSource(
          (raw.campaigns ?? []).map(mapPartyCampaign),
        );
        lobbyists.value = (raw.lobbyists ?? []).map(mapPartyLobbyist);
      }
    } catch (err) {
      error.value = err as ApiError;
    } finally {
      pending.value = false;
    }
  })();

  return {
    party: readonly(party) as Readonly<Ref<Party | null>>,
    candidates: readonly(candidates) as Readonly<Ref<Candidate[]>>,
    campaigns: readonly(campaigns) as Readonly<Ref<Campaign[]>>,
    lobbyists: readonly(lobbyists) as Readonly<Ref<Lobbyist[]>>,
    pending: readonly(pending) as Readonly<Ref<boolean>>,
    error: readonly(error) as Readonly<Ref<ApiError | null>>,
  };
}
